import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest, type User } from "@/types";
import { getNextPaginatePage } from "@/utils";

import { type News } from "@/modules/news/user/types";

export type NewsList = Paginate<News<true, true>>;

export const news = createQueryKeys("news", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => ({
    queryKey: [id],
    contextQueries: {
      likes: null,
      likesInfinite: null,
    },
  }),
  allInfinite: (filter?: SearchRequest) => [{ filter }],
});

const include = "categories,user,likedUsers";

export const fetchNewsListQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...news.all(page, search),
    queryFn: () =>
      api
        .get<NewsList>(`/news`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchNewsList = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchNewsListQueryOptions(page, search));

export const fetchInfiniteNewsListQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...news.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<NewsList>(`/news`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchInfiniteNewsList = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchInfiniteNewsListQueryOptions(search) });

export const fetchNewsQueryOptions = (id?: number) =>
  queryOptions({
    ...news.one(id),
    queryFn: () =>
      api
        .get<News<true, true>>(`/news/${id}`, {
          params: {
            include,
          },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchNews = (id?: number) =>
  useQuery(fetchNewsQueryOptions(id));

export const useFetchBookmarkEntries = () =>
  useFetchNewsList({ filters: { is_bookmarked: 1 } });

export const useBookmarkNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.put(`/news/${id}/bookmark`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const useUnbookmarkNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.delete(`/news/${id}/bookmark`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const fetchInfiniteNewsUsersLikedQueryOptions = (id?: number) =>
  infiniteQueryOptions({
    ...news.one(id)._ctx.likesInfinite,
    queryFn: ({ pageParam }) =>
      api
        .get<Paginate<User<true>>>(`/news/${id}/likes`, {
          params: { page: pageParam, include: "position" },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
    enabled: id !== undefined,
  });

export const useFetchNewsUsersLiked = (id?: number) =>
  useInfiniteQuery({
    ...fetchInfiniteNewsUsersLikedQueryOptions(id),
  });

export const useLikeNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.post(`/news/${id}/likes`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};

export const useUnlikeNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      api.delete(`/news/${id}/likes`).then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: news.all._def,
      });
      queryClient.invalidateQueries(news.one(id));
    },
  });
};
